export function youtubeAPIReady(): Promise<typeof YT> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube Iframe API can only be loaded in the browser'))
  }

  // If ready, resolve immediately.
  if (window.YT?.Player) {
    return Promise.resolve(window.YT)
  }

  // Create the resolver queue if needed.
  if (!window.__ytResolvers) {
    window.__ytResolvers = []
  }

  // If already flagged as ready but YT.Player not present yet (rare race), poll shortly.
  if (window.__ytReady && !window.YT?.Player) {
    return new Promise((resolve, reject) => {
      const start = performance.now()
      const tick = () => {
        if (window.YT?.Player) {
          return resolve(window.YT!)
        }
        if (performance.now() - start > 5000) {
          return reject(new Error('YT ready flag set but Player missing'))
        }
        requestAnimationFrame(tick)
      }
      tick()
    })
  }

  // If loading is already in progress, just queue a resolver.
  if (window.__ytApiLoading) {
    return new Promise<typeof YT>(resolve => {
      window.__ytResolvers!.push(resolve)
    })
  }

  window.__ytApiLoading = true

  // Install/chain the global callback exactly once.
  const previous = window.onYouTubeIframeAPIReady
  window.onYouTubeIframeAPIReady = () => {
    try {
      previous?.()
    } catch {
      /* ignore */
    }
    window.__ytReady = true
    window.__ytApiLoading = false

    // Flush queued resolvers safely when YT is present
    if (window.YT?.Player) {
      const resolvers = window.__ytResolvers ?? []
      window.__ytResolvers = []
      for (const r of resolvers) {
        r(window.YT!)
      }
    }
  }

  // Inject the script if not present
  const src = 'https://www.youtube.com/iframe_api'
  if (!document.querySelector(`script[src="${src}"]`)) {
    const tag = document.createElement('script')
    tag.src = src
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(tag, firstScript)
  }

  // Return a promise that resolves when the global callback fires or if YT becomes available earlier
  return new Promise<typeof YT>((resolve, reject) => {
    // If the API beats us to it (rare, but can happen with HMR), resolve quickly
    if (window.YT?.Player) {
      window.__ytReady = true
      window.__ytApiLoading = false
      return resolve(window.YT!)
    }
    window.__ytResolvers!.push(resolve)

    // Optional safety timeout (helps debug)
    setTimeout(() => {
      if (!window.YT?.Player) {
        reject(new Error('Timed out waiting for YouTube Iframe API'))
      }
    }, 15000)
  })
}

export function createYouTubePlayer(
  containerId: string,
  videoId: string,
  onPlayerReady?: (event: YT.PlayerEvent) => void,
  onStateChange?: (event: YT.OnStateChangeEvent) => void
): Promise<YT.Player> {
  return youtubeAPIReady().then(YTNS => {
    return new Promise<YT.Player>(resolve => {
      const player = new YTNS.Player(containerId, {
        height: '100%',
        width: '100%',
        videoId,
        playerVars: {
          rel: 0,
          controls: 1,
          modestbranding: 1,
          playsinline: 1,
          origin: window.location.origin,
          iv_load_policy: 3,
          cc_load_policy: 0
        },
        events: {
          onReady: (event: YT.PlayerEvent) => {
            onPlayerReady?.(event)
            resolve(player)
          },
          onStateChange: (event: YT.OnStateChangeEvent) => onStateChange?.(event)
        }
      } satisfies YT.PlayerOptions)
    })
  })
}
